import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

import type { DualPathMutationResult } from '../dualPathMutationResult';

export interface InsertObligationChangeAttestationTRPCInput {
  ObligationChangeId: string;
}

/**
 * Maps the TRPC mutation response to match the GraphQL mutation structure so
 * consumers can branch on success/error without knowing which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: { Id: string }) => {
  return {
    insertChildObligationChangeAttestation: {
      __typename: 'IdOutput' as const,
      Id: trpcData.Id,
    },
  };
};

export type InsertObligationChangeAttestationResult = ReturnType<
  typeof mapTrpcResponseToGraphQL
>;
export type InsertObligationChangeAttestationCompatibilityResult =
  DualPathMutationResult<InsertObligationChangeAttestationResult>;

export const useInsertObligationChangeAttestationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationChangeAttestation.insert.mutationOptions({
      onSuccess: async () => {
        // Legacy Apollo path evicted the coarse `obligation_change` field,
        // so invalidate the whole obligation-change sub-router (the family
        // backing getObligationChangeById).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationChange.pathKey(),
        });
      },
    }),
  });

  return {
    insertObligationChangeAttestation: async (
      variables: InsertObligationChangeAttestationTRPCInput
    ): Promise<InsertObligationChangeAttestationResult> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
